<template lang="pug">
FRouterLink.FUseCaseCard(
  :location="link?.location"
  hide-arrow
  :style="style"
  @mouseenter="isHovered = true"
  @mouseleave="isHovered = false"
)
  FCard.FUseCaseCard__card(
    :color="color + '--light-2'"
    :width="width"
  )
    .FUseCaseCard__header(:style="headerStyle")
      span {{ categoryText }}
    .FUseCaseCard__content
      .FUseCaseCard__body
        .FUseCaseCard__imageContainer
          FImage.FUseCaseCard__image(
            v-bind="image"
            :height="smAndDown('100%', '240')"
            fit="cover"
          )
        .FUseCaseCard__text
          span.FUseCaseCard__body__cityName {{ cityName }}
          h4.FUseCaseCard__body__title {{ title }}
          FDivider(:color="props.color + '--light-1'")
      .FUseCaseCard__footer
        FLink(
          v-bind="link"
          :color="props.color + (isHovered ? '--light-2' : '--dark-2')"
          :hover-color="props.color + (isHovered ? '--light-1' : '--dark-1')"
        ) {{ link.text }}
</template>

<style lang="stylus">
.FUseCaseCard
  padding 0
  position relative

.FUseCaseCard__card
  overflow hidden
  slide-background-animation(var(--FUseCaseCard--textColor))

  &::after
    z-index 0

  &:hover
    .FUseCaseCard__body__cityName,
    .FUseCaseCard__body__title,
    .FUseCaseCard__footer
      color var(--FUseCaseCard--color)

    .FUseCaseCard__image
      scale 1.1

.FUseCaseCard__header
  position relative
  text-transform uppercase
  text-align center
  transition background-color 0.5s var(--transition--ease-out)
  background-color var(--FUseCaseCard--header--color)
  use-font('overline')
  color var(--FUseCaseCard--header--textColor)
  padding rem(16)
  z-index 1

.FUseCaseCard__content
  position relative

.FUseCaseCard__body
  position relative
  display flex
  flex-direction column
  padding rem(16)
  padding-bottom 0
  z-index 1

  .FUseCaseCard__body__cityName,
  .FUseCaseCard__body__title,
  .FUseCaseCard__footer.FLink
    color var(--FUseCaseCard--textColor)
    transition color 0.5s var(--transition--ease-out)

.FUseCaseCard__imageContainer
  overflow hidden
  border-radius rem(16)

.FUseCaseCard__image
  transition scale 0.5s var(--transition--ease-out)

.FUseCaseCard__text
  display flex
  flex-direction column
  padding rem(16)
  z-index 1

.FUseCaseCard__body__cityName
  use-font('subtitle-1')
  margin-bottom rem(8)

.FUseCaseCard__footer
  padding 0 rem(32)
  padding-bottom rem(32)
  text-transform uppercase

  .FLink
    z-index 1
</style>

<script setup lang="ts">
import type { FImageProps } from '@fifteen/design-system-vue';
import type { FcLinkProps } from '@/components/molecules/FcLink.vue';

export interface FUseCaseCardProps {
  /**
   * Name of the service category
   */
  categoryName: ServiceCategory | 'augmented_bike_network';
  /**
   * Color of the card
   */
  color?: ColorDesignToken;
  /**
   * Width of the card
   */
  width?: string | number;
  /**
   * Image of the card
   */
  image: FImageProps;
  /**
   * Name of the city
   */
  cityName: string;
  /**
   * Title of the card
   */
  title: string;
  /**
   * Link of the card
   */
  link?: FcLinkProps & { text: string };
}

const props = withDefaults(defineProps<FUseCaseCardProps>(), {
  color: 'primary',
  link: () => ({ text: '' }),
  width: '528px',
});

const { t } = useI18n();
const { smAndDown } = useFBreakpoints();

const categoryText = computed(() =>
  t(`use_case__category__${props.categoryName}`)
);

const headerBackground = computed(
  () =>
    ({
      sharing: isHovered.value ? 'info--dark-1' : 'info',
      leasing: isHovered.value ? 'success--dark-1' : 'success',
      train_station: isHovered.value ? 'secondary--dark-1' : 'secondary',
      augmented_bike_network: isHovered.value
        ? 'secondary--light-1'
        : 'secondary--light-2',
    }[props.categoryName])
);
const headerTextColor = computed(() =>
  getCssColor(
    {
      sharing: 'info--light-2',
      leasing: 'success--dark-2',
      train_station: 'primary',
      augmented_bike_network: 'secondary',
    }[props.categoryName]
  )
);

const isHovered = ref(false);

const style = computed<Style>(() => ({
  '--FUseCaseCard--color': getCssColor(props.color + '--light-2'),
  '--FUseCaseCard--textColor': getCssColor(props.color + '--dark-2'),
}));
const headerStyle = computed<Style>(() => ({
  '--FUseCaseCard--header--color': getCssColor(headerBackground.value),
  '--FUseCaseCard--header--textColor': getCssColor(headerTextColor.value),
}));
</script>
